import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Container, 
    Box, 
    Card, 
    DialogContent, 
    DialogTitle, 
    Dialog, 
    Typography, 
    Button, 
    IconButton, 
    DialogActions, 
    Divider, 
    CircularProgress, 
    Avatar, 
    Grid, 
    Grow,
    Stack,
    Tabs,
    Tab,
    Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import moment from 'moment';

import { NavbarInicio } from '../ui/NavbarInicio';
import { usePermisos } from '../../hooks/usePermisos';
import { NotFoundScreen } from '../auth/NotFoundScreen';
import { DeleteCitas, GetCitMisCitas } from '../../actions/citas/CitCitasActions';
import { CardMantenimiento } from '../ui/CardMantenimiento';


export const CitasScreen = () => {
    const navigate = useNavigate();
    const { banderaCitas, banderaOnlineCitas, loading, idUsuarioCitas } = usePermisos();

    const [loadCitas, setLoadCitas] = useState(false);
    const [data, setData] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
    const [loadingCancel, setLoadingCancel] = useState(false);

    const [tabsActivas, setTabsActivas] = useState({});
    const getTab = (id) => tabsActivas[id] ?? 'qr';
    const setTab = (id, value) => setTabsActivas(prev => ({ ...prev, [id]: value }));

    const getClienteId = (id) => {
        if (!id) return null;
        const idStr = id.toString();
        return idStr.includes('/') ? idStr.split('/').pop() : idStr;
    };

    const formatDate = (inicio) => moment(inicio).format("DD/MM/YYYY");
    const formatTime = (inicio) => moment(inicio).format("HH:mm");

    const handleOpenDialog = (id) => {
        setSelectedAppointmentId(id);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedAppointmentId(null);
        setLoadingCancel(false);
    };

    const handleCancelAppointment = async (id) => {
        setLoadingCancel(true);
        try {
            const response = await DeleteCitas(id);
            if (response.data?.success) {
                setTimeout(() => {
                    const updated = data.filter(cita => String(cita.id) !== String(id));
                    setData(updated);
                    handleCloseDialog();
                    setLoadingCancel(false);
                }, 600);
            } else {
                setLoadingCancel(false);
            }
        } catch (error) {
            console.error("Error al cancelar la cita:", error);
            setLoadingCancel(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            if (!idUsuarioCitas) {
                setLoadCitas(false);
                return;
            }
            setLoadCitas(true);
            const clienteId = getClienteId(idUsuarioCitas);
            try {
                const response = await GetCitMisCitas(clienteId);
                if (response && response.status === 200) {
                    setTimeout(() => {
                        if (response.data && Array.isArray(response.data.data)) {
                            setData(response.data.data);
                        } else {
                            console.error("Los datos de citas no son un array:", response.data);
                            setData([]);
                        }
                        setLoadCitas(false);
                    }, 1000);
                } else {
                    console.error("Error en la respuesta del servidor:", response);
                    setData([]);
                    setLoadCitas(false);
                }
            } catch (error) {
                console.error("Error al obtener las citas:", error);
                setData([]);
                setLoadCitas(false);
            }
        }
        fetchData();
    }, [idUsuarioCitas]);

    // Componente para campos de información con iconos
    const InfoField = ({ icon: Icon, label, value, compact = false }) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: compact ? 1.5 : 1.8,
                borderRadius: 2,
                bgcolor: '#f8f9fa',
                border: '1px solid #e0e0e0',
            }}
        >
            <Avatar sx={{ bgcolor: '#121528', width: 36, height: 36 }}>
                <Icon sx={{ fontSize: 18, color: 'white' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.65rem',
                        letterSpacing: '0.5px',
                        display: 'block',
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    variant="body2"
                    component="div"
                    sx={{
                        color: '#121528',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        lineHeight: 1.3,
                        wordBreak: 'break-word',
                    }}
                >
                    {value || 'No especificado'}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <>
            <NavbarInicio />

            {(loading || loadCitas) && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <CircularProgress sx={{ color: 'white' }} />
                </Box>
            )}

            {(!loading && !banderaCitas) && <NotFoundScreen />}

            {(!loading && banderaCitas && !loadCitas) && (
                !banderaOnlineCitas
                    ? (
                        <Container maxWidth="xl" sx={{ mt: { md: '10em', xs: '8em' } }}>
                            <CardMantenimiento />
                        </Container>
                    )
                    : (
                        <Container sx={{ pt: { xs: 10, sm: 0, d: 4 } }} maxWidth="xl">

                            {/* Encabezado */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'center', sm: 'center' },
                                    gap: 2,
                                    mt: { xs: 6, sm: 16, md: 20 },
                                    mb: 4,
                                }}
                            >
                                <Typography variant="h4" fontWeight={700} sx={{ color: '#eeeeefff' }}>
                                    Mis citas
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/nuevaCita/')}
                                    sx={{
                                        bgcolor: '#fff',
                                        color: 'black',
                                        px: 4,
                                        py: 1.2,
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        alignSelf: { xs: 'stretch', sm: 'auto' },
                                        '&:hover': { bgcolor: '#e8e8e8ff' },
                                    }}
                                >
                                    Nueva cita
                                </Button>
                            </Box>

                            {/* Estado vacío */}
                            {data.length === 0 && (
                                <Box width="100%" mt={20} textAlign="center" py={6}>
                                    <Avatar sx={{ bgcolor: '#f0f4ff', width: 80, height: 80, margin: '0 auto', border: '2px solid #121528' }}>
                                        <CalendarMonthIcon sx={{ color: '#121528', fontSize: 40 }} />
                                    </Avatar>
                                    <Typography variant="h5" mt={3} sx={{ color: '#fff', fontWeight: 600 }}>
                                        No tienes citas agendadas
                                    </Typography>
                                    <Typography variant="body1" mt={1} sx={{ color: '#c3c3c3' }}>
                                        Agenda una nueva cita para comenzar
                                    </Typography>
                                </Box>
                            )}

                            {/* Grid de citas */}
                            <Grid container spacing={3} mb={3} mt={3}>
                                {[...data]
                                    .sort((a, b) => moment(a.inicio).valueOf() - moment(b.inicio).valueOf())
                                    .map((item) => (
                                        <Grow key={item.id} in style={{ transformOrigin: '0 0 0' }} timeout={1000}>
                                            <Grid item xs={12} sm={6} md={4} lg={3} display="flex" justifyContent="center" alignItems="stretch">
                                                <Card
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 400,
                                                        borderRadius: 3,
                                                        boxShadow: '0 8px 24px rgba(18, 21, 40, 0.12)',
                                                        overflow: 'hidden',
                                                        background: '#252b50',
                                                    }}
                                                >
                                                    {/* Header */}
                                                    <Box
                                                        sx={{
                                                            background: item.estado?.toLowerCase() === 'asistio'
                                                                ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #1b5e20 100%)'
                                                                : 'linear-gradient(135deg, #121528 0%, #1e2442 50%, #121528 100%)',
                                                            color: 'white',
                                                            p: 2.5,
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                bgcolor: 'rgba(255,255,255,0.15)',
                                                                p: 1.2,
                                                                borderRadius: '50%',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            {item.estado?.toLowerCase() === 'asistio'
                                                                ? <CheckCircleIcon sx={{ fontSize: 28 }} />
                                                                : <CalendarMonthIcon sx={{ fontSize: 28 }} />
                                                            }
                                                        </Box>
                                                        <Typography variant="h6" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: '1px' }}>
                                                            Cita
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: '1px' }}>
                                                            {item.id}
                                                        </Typography>
                                                    </Box>

                                                    {/* Contenido */}
                                                    <Box
                                                        sx={{
                                                            p: 2.5,
                                                            bgcolor: 'white',
                                                            borderRadius: 3,
                                                            borderTopLeftRadius: 3,
                                                            borderTopRightRadius: 3,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <Stack spacing={1.5}>

                                                            {/* Fecha y Hora — FIX: flexWrap + gap fijo */}
                                                            <Box
                                                                sx={{
                                                                    p: 1.5,
                                                                    bgcolor: '#f8f9fa',
                                                                    borderRadius: 2,
                                                                    border: '1px solid #e0e0e0',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    flexWrap: 'wrap',
                                                                    gap: 2,
                                                                }}
                                                            >
                                                                {/* Fecha */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                    <Box sx={{ display: 'inline-flex', bgcolor: '#121528', p: 1, borderRadius: '50%', color: 'white' }}>
                                                                        <CalendarMonthIcon sx={{ fontSize: 20 }} />
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block' }}>
                                                                            Fecha
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: '#121528', fontWeight: 600, fontSize: '0.85rem' }}>
                                                                            {formatDate(item.inicio)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>

                                                                {/* Hora */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                    <Box sx={{ display: 'inline-flex', bgcolor: '#121528', p: 1, borderRadius: '50%', color: 'white' }}>
                                                                        <AccessTimeIcon sx={{ fontSize: 20 }} />
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block' }}>
                                                                            Hora
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: '#121528', fontWeight: 600, fontSize: '0.85rem' }}>
                                                                            {formatTime(item.inicio)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>

                                                            {/* Oficina */}
                                                            <InfoField
                                                                icon={BusinessIcon}
                                                                label="Oficina"
                                                                value={item.oficina_descripcion || 'N/A'}
                                                                compact
                                                            />

                                                            {/* Servicio */}
                                                            <InfoField
                                                                icon={DescriptionIcon}
                                                                label="Servicio"
                                                                value={item.cit_servicio_descripcion || 'N/A'}
                                                                compact
                                                            />

                                                            {/* Notas / Expediente */}
                                                            {(() => {
                                                                const esExp = item?.cit_servicio_descripcion?.toLowerCase().includes('expediente');
                                                                const textoCompleto = item.notas ?? 'Sin notas';
                                                                const truncado = textoCompleto.length > 50;
                                                                const textoMostrado = truncado ? textoCompleto.slice(0, 50) + '...' : textoCompleto;

                                                                return (
                                                                    <Tooltip
                                                                        title={truncado ? textoCompleto : ''}
                                                                        placement="top"
                                                                        arrow
                                                                        disableHoverListener={!truncado}
                                                                    >
                                                                        <Box sx={{
                                                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                                                            p: 1.5, borderRadius: 2, bgcolor: '#f8f9fa',
                                                                            border: '1px solid #e0e0e0',
                                                                            cursor: truncado ? 'help' : 'default'
                                                                        }}>
                                                                            <Avatar sx={{ bgcolor: '#121528', width: 36, height: 36 }}>
                                                                                {esExp
                                                                                    ? <DescriptionIcon sx={{ fontSize: 18, color: 'white' }} />
                                                                                    : <NoteAltIcon sx={{ fontSize: 18, color: 'white' }} />
                                                                                }
                                                                            </Avatar>
                                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                <Typography variant="caption" sx={{
                                                                                    color: '#6c757d', fontWeight: 600, textTransform: 'uppercase',
                                                                                    fontSize: '0.65rem', letterSpacing: '0.5px', display: 'block'
                                                                                }}>
                                                                                    {esExp ? 'Expedientes' : 'Notas'}
                                                                                </Typography>
                                                                                <Typography variant="body2" component="div" sx={{
                                                                                    color: '#121528', fontWeight: 500, fontSize: '0.9rem',
                                                                                    lineHeight: 1.3, wordBreak: 'break-word'
                                                                                }}>
                                                                                    {textoMostrado}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Tooltip>
                                                                );
                                                            })()}
                                                        </Stack>

                                                        {/* Tabs + códigos — FIX: centrado y responsivo */}
                                                        {item?.codigo_acceso_url && item?.codigo_barras_url ? (
                                                            <Box sx={{ mt: 1, width: '100%' }}>
                                                                <Tabs
                                                                    value={getTab(item.id)}
                                                                    onChange={(_, v) => setTab(item.id, v)}
                                                                    centered
                                                                    sx={{
                                                                        mb: 2,
                                                                        '& .MuiTab-root': {
                                                                            fontWeight: 600,
                                                                            fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.62rem' },
                                                                            minWidth: 0,
                                                                            px: { xs: 0.5, sm: 1.5 },
                                                                            letterSpacing: 0,
                                                                        },
                                                                        '& .Mui-selected': { color: '#0e0e0eff' },
                                                                        '& .MuiTabs-indicator': { backgroundColor: '#000' },
                                                                    }}
                                                                >
                                                                    <Tab label="Código acceso" value="qr" />
                                                                    <Tab label="Código asistencia" value="barras" />
                                                                </Tabs>

                                                                {getTab(item.id) === 'qr' && (
                                                                    <Box
                                                                        sx={{
                                                                            p: 2,
                                                                            bgcolor: '#f8f9fa',
                                                                            borderRadius: 2,
                                                                            border: '1px solid #dee2e6',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            width: '100%',
                                                                            boxSizing: 'border-box',
                                                                        }}
                                                                    >
                                                                        <img
                                                                            alt="qr"
                                                                            src={item.codigo_acceso_url}
                                                                            style={{ width: '60%', maxWidth: 200, borderRadius: 8, display: 'block' }}
                                                                        />
                                                                    </Box>
                                                                )}

                                                                {getTab(item.id) === 'barras' && (
                                                                    <Box
                                                                        sx={{
                                                                            p: 3,
                                                                            bgcolor: '#f8f9fa',
                                                                            borderRadius: 2,
                                                                            border: '1px solid #dee2e6',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            width: '100%',
                                                                            boxSizing: 'border-box',
                                                                        }}
                                                                    >
                                                                        <img
                                                                            alt="barras"
                                                                            src={item.codigo_barras_url}
                                                                            style={{ width: '80%', maxWidth: 200, borderRadius: 8, display: 'block' }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </Box>

                                                        ) : item?.codigo_acceso_url ? (
                                                            // Solo QR — sin tabs
                                                            <Box
                                                                sx={{
                                                                    mt: 2,
                                                                    p: 2,
                                                                    bgcolor: '#f8f9fa',
                                                                    borderRadius: 2,
                                                                    border: '1px solid #dee2e6',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                    width: '100%',
                                                                    boxSizing: 'border-box',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: '#6c757d',
                                                                        fontWeight: 600,
                                                                        mb: 1,
                                                                        textTransform: 'uppercase',
                                                                        fontSize: '0.65rem',
                                                                        display: 'block',
                                                                        textAlign: 'center',
                                                                    }}
                                                                >
                                                                    Código de acceso
                                                                </Typography>
                                                                <img
                                                                    alt="qr"
                                                                    src={item.codigo_acceso_url}
                                                                    style={{ width: '60%', maxWidth: 200, borderRadius: 8, display: 'block' }}
                                                                />
                                                            </Box>
                                                        ) : null}

                                                        {/* Botón cancelar */}
                                                        <Box sx={{ mt: 'auto', pt: 1 }}>
                                                            {item.puede_cancelarse && (
                                                                <Button
                                                                    variant="contained"
                                                                    color="error"
                                                                    fullWidth
                                                                    onClick={() => handleOpenDialog(item.id)}
                                                                    sx={{
                                                                        mt: 2,
                                                                        py: 1.2,
                                                                        borderRadius: 2,
                                                                        fontWeight: 600,
                                                                        fontSize: '0.9rem',
                                                                    }}
                                                                >
                                                                    Cancelar cita
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        </Grow>
                                    ))}
                            </Grid>

                            {/* Diálogo de Cancelación */}
                            <Dialog
                                open={openDialog}
                                onClose={loadingCancel ? undefined : handleCloseDialog}
                                fullWidth
                                maxWidth="xs"
                                PaperProps={{ sx: { borderRadius: 3 } }}
                            >
                                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box display="flex" alignItems="center">
                                        <Typography variant="h6" fontWeight="bold">Cancelar Cita</Typography>
                                    </Box>
                                    <IconButton onClick={handleCloseDialog} sx={{ color: 'text.secondary' }} disabled={loadingCancel}>
                                        <CloseIcon />
                                    </IconButton>
                                </DialogTitle>

                                <Divider />

                                <DialogContent>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, mb: 3 }}>
                                        <Box sx={{ mb: 2 }}>
                                            <WarningAmberIcon sx={{ fontSize: 82 }} />
                                        </Box>
                                        <Typography align="center" variant="subtitle1" fontWeight={500}>
                                            ¿Desea cancelar la cita seleccionada?
                                        </Typography>
                                    </Box>
                                </DialogContent>

                                <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
                                    <Button
                                        onClick={handleCloseDialog}
                                        color="inherit"
                                        disabled={loadingCancel}
                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Cancelar
                                    </Button>
                                    <LoadingButton
                                        onClick={() => selectedAppointmentId && handleCancelAppointment(selectedAppointmentId)}
                                        loading={loadingCancel}
                                        variant="contained"
                                        color="primary"
                                        sx={{ ml: 2, minWidth: 120, textTransform: 'none', fontWeight: 600 }}
                                        autoFocus
                                    >
                                        Confirmar
                                    </LoadingButton>
                                </DialogActions>
                            </Dialog>

                        </Container>
                    )
            )}
        </>
    );
};